import { TrustedDevices } from "@/components/LoginSecuritySettings";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { AuthImage } from "@/components/AuthImage";
import { NotificationsToggle } from "@/components/EnableNotifications";
import {
  Camera, Lock, LogOut, Save, ShieldCheck, Banknote,
  Calendar as CalIcon, Briefcase, Clock, ClipboardCheck, Trash2,
  Users as UsersIcon, ChevronRight, LifeBuoy, Sun, Moon, Smartphone,
} from "lucide-react";
import { useTheme } from "@/lib/theme";
import { LicenceCard, UniformCard, MyContractCard } from "@/components/HrCards";
import { BiometricUnlockCard } from "@/components/BiometricUnlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bird } from "lucide-react";
import { toast } from "sonner";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });

const ROLE_LABEL = {
  super_admin: "Platform admin",
  admin: "Company admin",
  office: "Office staff",
  field: "Field worker",
};

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [avatarPath, setAvatarPath] = useState(user?.avatar_path || null);
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await api.get("/users/me/stats"); if (!cancelled) setStats(r.data); } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const saveName = async (e) => {
    e?.preventDefault();
    if (!name.trim() || name === user.name) return;
    setSavingName(true);
    try {
      await api.patch("/users/me", { name: name.trim() });
      await refresh();
      toast.success("Name updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update name");
    } finally { setSavingName(false); }
  };

  const onPickFile = () => fileRef.current?.click();

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "avatar");
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = up.data.storage_path;
      await api.patch("/users/me", { avatar_path: path });
      setAvatarPath(path);
      await refresh();
      toast.success("Profile photo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearAvatar = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    try {
      await api.patch("/users/me", { avatar_path: "" });
      setAvatarPath(null);
      await refresh();
      toast.success("Photo removed");
    } catch (e) { toast.error("Could not remove"); }
  };

  const initials = (user.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div data-testid="profile-page" className="px-4 py-4 md:p-6 lg:p-10 md:max-w-5xl md:mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ my profile"
        title="Profile"
        subtitle="Your photo, name, password and personal stats — all in one place."
      />

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Avatar + identity column */}
        <div className="space-y-4">
          <div className="hard-card p-6 text-center" data-testid="profile-avatar-card">
            <div className="relative inline-block">
              {avatarPath ? (
                <AuthImage path={avatarPath} alt={user.name} className="w-32 h-32 object-cover border-4 border-[#09090B] mx-auto" />
              ) : (
                <div className="w-32 h-32 bg-[#FF5F15] border-4 border-[#09090B] flex items-center justify-center mx-auto">
                  <span className="font-display font-black text-5xl text-white">{initials}</span>
                </div>
              )}
              <button
                onClick={onPickFile}
                disabled={uploading}
                className="absolute -bottom-2 -right-2 w-10 h-10 bg-[#FF5F15] hover:bg-[#E65100] text-white border-4 border-white flex items-center justify-center disabled:opacity-50"
                aria-label="Upload photo"
                data-testid="profile-photo-upload-button"
              >
                <Camera className="w-4 h-4" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadAvatar} data-testid="profile-photo-input" />
            </div>
            <div className="mt-5 font-display font-black text-xl uppercase tracking-tight" data-testid="profile-name-display">{user.name}</div>
            <div className="text-xs text-zinc-500 font-mono mt-1">{user.email}</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-zinc-100 border border-zinc-300 rounded-sm">
              {user.role === "super_admin" && <ShieldCheck className="w-3 h-3 text-[#FF5F15]" />}
              {ROLE_LABEL[user.role] || user.role}
            </div>
            {avatarPath && (
              <div className="mt-3">
                <button onClick={clearAvatar} className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-red-600 inline-flex items-center gap-1" data-testid="profile-photo-remove">
                  <Trash2 className="w-3 h-3" /> Remove photo
                </button>
              </div>
            )}
          </div>

          <div className="hard-card p-5">
            <div className="label-tag mb-3">Notifications</div>
            <NotificationsToggle />
            <p className="mt-3 text-xs text-zinc-500">Get pinged on this device when you&rsquo;re mentioned, dispatched, or a toolbox talk drops.</p>
          </div>

          <Link
            to="/app/crew"
            className="hard-card p-5 flex items-center gap-3 hover:bg-zinc-50 transition group"
            data-testid="profile-crew-link"
          >
            <div className="w-10 h-10 bg-[#09090B] flex items-center justify-center shrink-0">
              <UsersIcon className="w-5 h-5 text-[#FF5F15]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase tracking-tight text-sm">Crew directory</div>
              <div className="text-[11px] text-zinc-500">See everyone&rsquo;s photo + tomorrow&rsquo;s job</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-[#FF5F15]" />
          </Link>

          <Link
            to="/app/support"
            className="hard-card p-5 flex items-center gap-3 hover:bg-zinc-50 transition group"
            data-testid="profile-support-link"
          >
            <div className="w-10 h-10 bg-[#FF5F15] flex items-center justify-center shrink-0">
              <LifeBuoy className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase tracking-tight text-sm">Support</div>
              <div className="text-[11px] text-zinc-500">Found a bug? Need a hand? Open a ticket.</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-[#FF5F15]" />
          </Link>

          <Button
            onClick={logout}
            variant="outline"
            className="w-full rounded-sm h-11 uppercase font-bold tracking-wider border-zinc-300 text-zinc-700 hover:bg-zinc-100"
            data-testid="profile-signout-button"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>

        {/* Right column — name, password, stats, leave, pay */}
        <div className="space-y-4">
          {/* Appearance theme picker */}
          <ThemePicker />

          {/* Company Kiri assistant switch — admin/super_admin only */}
          {(user.role === "admin" || user.role === "super_admin") && user.org_id && <CompanyKiriCard />}

          {/* Edit name */}
          <form onSubmit={saveName} className="hard-card p-5">
            <div className="label-tag mb-3">Your name</div>
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm border-zinc-400 h-11 flex-1" placeholder="Your name" data-testid="profile-name-input" />
              <Button
                type="submit"
                disabled={savingName || !name.trim() || name === user.name}
                className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase font-bold tracking-wider px-4"
                data-testid="profile-name-save"
              >
                <Save className="w-4 h-4 mr-1.5" /> Save
              </Button>
            </div>
          </form>

          {/* Personal stats */}
          {stats && (
            <div className="hard-card p-5" data-testid="profile-stats-card">
              <div className="label-tag mb-3">Your work — at a glance</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat icon={Clock} label="Hours this week" value={stats.hours_week.toFixed(1)} testid="stat-hours-week" />
                <Stat icon={CalIcon} label="Hours this month" value={stats.hours_month.toFixed(1)} testid="stat-hours-month" />
                <Stat icon={CalIcon} label="Hours YTD" value={stats.hours_year.toFixed(1)} testid="stat-hours-year" />
                <Stat icon={Briefcase} label="Jobs worked" value={stats.jobs_count} testid="stat-jobs" />
                <Stat icon={Clock} label="Timesheets" value={stats.timesheets_count} testid="stat-timesheets" />
                <Stat icon={ClipboardCheck} label="Safety forms" value={stats.safety_forms_count} testid="stat-safety" />
              </div>
            </div>
          )}

          {/* Leave balances + pay rate (read-only) */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="hard-card p-5">
              <div className="label-tag mb-3">Leave balances <span className="text-zinc-400 normal-case">· days</span></div>
              <div className="space-y-2">
                <LeaveRow label="Annual" value={user.leave_balances?.annual ?? 0} />
                <LeaveRow label="Sick" value={user.leave_balances?.sick ?? 0} />
                <LeaveRow label="Bereavement" value={user.leave_balances?.bereavement ?? 0} />
              </div>
              <p className="mt-3 text-[11px] text-zinc-500">Your office sets these. Need a top-up? Ask them.</p>
            </div>

            <div className="hard-card p-5">
              <div className="label-tag mb-3">Pay rate</div>
              <div className="metric text-4xl font-black" data-testid="profile-pay-rate">
                {user.hourly_rate > 0 ? <>{NZD.format(user.hourly_rate)}<span className="text-zinc-500 text-sm font-mono font-normal">/hr</span></> : <span className="text-zinc-400 text-base">Not set</span>}
              </div>
              <p className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1"><Banknote className="w-3 h-3" /> Office-managed. Check your contract.</p>
            </div>
          </div>

          {/* Licence, uniform sizes & contract */}
          <LicenceCard me={user} />
          <UniformCard me={user} />
          <MyContractCard me={user} />

          {/* Biometric unlock */}
          <BiometricUnlockCard />
          <TrustedDevices />

          {/* Change password */}
          <ChangePasswordCard />

          {/* Request account deletion */}
          <DeleteAccountCard />
        </div>
      </div>
    </div>
  );
}

// Lets a signed-in user request deletion of their account + personal data.
// Fieldlink is a B2B tool so this is a verified request actioned by our team
// (work records owned by the employer may be retained for legal compliance).
function DeleteAccountCard() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/account/deletion-request")
      .then((r) => { if (r.data?.pending) setDone(true); })
      .catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/deletion-request", {}, { __flOnlineOnly: true });
      setDone(true);
      setConfirming(false);
      toast.success(data?.message || "Your deletion request has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your request. Please try again or email support@fieldlink.co.nz.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hard-card p-5 border-red-200" data-testid="profile-delete-account-card">
      <div className="label-tag mb-3 text-red-600 flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete account</div>
      {done ? (
        <p className="text-sm text-zinc-600" data-testid="delete-account-requested">
          Your deletion request has been received. Our team will email you to confirm and will action it within 30 days.
        </p>
      ) : (
        <>
          <p className="text-sm text-zinc-600">
            Request deletion of your account and personal data. We&rsquo;ll email you to confirm first.
            Work records owned by your organisation (timesheets, safety, invoices) may be kept to meet legal requirements —
            see the <Link to="/delete-account" className="font-bold text-[#FF5F15]">deletion policy</Link>.
          </p>
          {!confirming ? (
            <Button
              variant="outline"
              onClick={() => setConfirming(true)}
              className="mt-4 rounded-sm h-11 uppercase font-bold tracking-wider border-red-300 text-red-600 hover:bg-red-50"
              data-testid="profile-delete-account-button"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Request account deletion
            </Button>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={submit}
                disabled={busy}
                className="rounded-sm h-11 uppercase font-bold tracking-wider bg-red-600 hover:bg-red-700 text-white"
                data-testid="profile-delete-account-confirm"
              >
                {busy ? "Sending…" : "Yes, request deletion"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="rounded-sm h-11 uppercase font-bold tracking-wider border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                data-testid="profile-delete-account-cancel"
              >
                Cancel
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Company Kiri assistant switch — lets org admins turn Kiri off/on for
// everyone in their organisation (platform-wide controls still apply on top).
function CompanyKiriCard() {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/org/me").then((r) => setEnabled(r.data?.kiri_enabled !== false)).catch(() => {});
  }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.patch("/org/me", { kiri_enabled: next });
      setEnabled(next);
      toast.success(next ? "Kiri turned on for your company" : "Kiri turned off for your company");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update Kiri setting");
    } finally { setBusy(false); }
  };

  if (enabled === null) return null;

  return (
    <div className="hard-card p-5" data-testid="profile-company-kiri-card">
      <div className="label-tag mb-3 flex items-center gap-1.5"><Bird className="w-3 h-3" /> Kiri assistant</div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold">{enabled ? "Kiri is on for your company" : "Kiri is off for your company"}</div>
          <p className="text-[11px] text-zinc-500 mt-0.5">Turn Kiri off if your team doesn&rsquo;t want the in-app assistant available.</p>
        </div>
        <Switch checked={enabled} disabled={busy} onCheckedChange={toggle} data-testid="profile-company-kiri-toggle" />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-zinc-200 p-3" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 text-[#FF5F15]" />
      <div className="metric text-2xl font-black mt-1">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function LeaveRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <span className="metric font-bold">{Number(value).toFixed(1)}</span>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/users/me/password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="hard-card p-5" data-testid="profile-change-password-card">
      <div className="label-tag mb-3 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Change password</div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="label-tag text-[9px]">Current</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 rounded-sm border-zinc-400" data-testid="profile-current-password" />
        </div>
        <div>
          <Label className="label-tag text-[9px]">New</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 rounded-sm border-zinc-400" data-testid="profile-new-password" />
        </div>
        <div>
          <Label className="label-tag text-[9px]">Confirm new</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-sm border-zinc-400" data-testid="profile-confirm-password" />
        </div>
      </div>
      <div className="mt-4">
        <Button
          type="submit"
          disabled={busy || !current || !next || !confirm}
          className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase font-bold tracking-wider"
          data-testid="profile-password-save"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>
      </div>
    </form>
  );
}


// Theme picker — Light / Dark / Auto (system).
function ThemePicker() {
  const { theme, setTheme } = useTheme();
  const opts = [
    { id: "light", label: "Light", desc: "Daytime", Icon: Sun },
    { id: "dark", label: "Dark", desc: "Black & hi-vis", Icon: Moon },
    { id: "auto", label: "Auto", desc: "Match phone", Icon: Smartphone },
  ];
  return (
    <div className="hard-card p-5" data-testid="profile-theme-picker">
      <div className="label-tag mb-3">Appearance</div>
      <div className="grid grid-cols-3 gap-2">
        {opts.map(({ id, label, desc, Icon }) => {
          const active = theme === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTheme(id)}
              data-testid={`theme-${id}`}
              className={`flex flex-col items-center justify-center gap-1 px-2 py-3 border-2 rounded-xl transition active:scale-[0.97] ${
                active
                  ? "border-[#FF5F15] bg-[#FF5F15]/10 text-[#FF5F15]"
                  : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-500 text-zinc-700 dark:text-zinc-300"
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={active ? 2.4 : 1.8} />
              <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400 normal-case font-normal">{desc}</span>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-3 leading-relaxed">
        Dark mode brings back the original brutalist black + hi-vis orange. Light mode keeps the new clean look. Auto follows your phone&apos;s system theme.
      </p>
    </div>
  );
}
